/* CBH — Review Carousel (no Swiper)
   - Prev/next buttons scroll by “page”
   - Optional dots + progress bar
   - Optional autoplay + loop wrap
   - Drag-to-scroll on desktop (prevents text highlighting)
   - Keyboard support (←/→) when focused
   - Respects prefers-reduced-motion
*/

(function () {
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function colsFor(sectionEl) {
    const m = parseInt(sectionEl.getAttribute('data-cols-mobile') || '1', 10);
    const t = parseInt(sectionEl.getAttribute('data-cols-tablet') || '2', 10);
    const d = parseInt(sectionEl.getAttribute('data-cols-desktop') || '3', 10);

    if (window.matchMedia('(min-width: 1280px)').matches) return d;
    if (window.matchMedia('(min-width: 768px)').matches) return t;
    return m;
  }

  function init(sectionEl) {
    const track = sectionEl.querySelector('[data-cbh-r1-track]');
    if (!track) return;

    const prev = sectionEl.querySelector('[data-cbh-r1-prev]');
    const next = sectionEl.querySelector('[data-cbh-r1-next]');
    const bar = sectionEl.querySelector('[data-cbh-r1-bar]');
    const dotsWrap = sectionEl.querySelector('[data-cbh-r1-dots]');

    const wantDots = sectionEl.getAttribute('data-dots') === '1';
    const wantBar = sectionEl.getAttribute('data-scrollbar') === '1';
    const wantAutoplay = sectionEl.getAttribute('data-autoplay') === '1' && !reduceMotion;
    const wantLoop = sectionEl.getAttribute('data-loop') === '1';

    // Cache measurements to avoid repeated layout reads during scroll.
    let _pageW = 0;
    let _max = 0;
    let _measuring = false;
    const measure = () => {
      _pageW = track.clientWidth;
      _max = Math.max(0, track.scrollWidth - track.clientWidth);
    };
    const scheduleMeasure = () => {
      if (_measuring) return;
      _measuring = true;
      requestAnimationFrame(() => {
        _measuring = false;
        measure();
        updateNav();
        updateScrollbar();
        updateDots();
      });
    };

    function pageWidth() {
      return _pageW || track.clientWidth;
    }

    function scrollByPage(dir) {
      const to = track.scrollLeft + dir * pageWidth();
      track.scrollTo({ left: to, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function updateNav() {
      if (!prev || !next) return;
      if (wantLoop) {
        prev.disabled = false;
        next.disabled = false;
        prev.setAttribute('aria-disabled', 'false');
        next.setAttribute('aria-disabled', 'false');
        return;
      }
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= (_max || 0) - 2;
      prev.disabled = atStart;
      next.disabled = atEnd;
      prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    function updateScrollbar() {
      if (!wantBar || !bar) return;
      const max = _max || 0;
      const p = max > 0 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
      bar.style.transform = `scaleX(${p})`;
    }

    function buildDots() {
      if (!wantDots || !dotsWrap) return;
      dotsWrap.innerHTML = '';

      const totalCards = track.children.length;
      const cols = Math.max(1, colsFor(sectionEl));
      const pages = Math.max(1, Math.ceil(totalCards / cols));

      for (let i = 0; i < pages; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cbh-r1__dot';
        btn.setAttribute('aria-label', `Go to reviews page ${i + 1}`);
        btn.addEventListener('click', () => {
          track.scrollTo({ left: i * pageWidth(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
        dotsWrap.appendChild(btn);
      }
      updateDots();
    }

    function updateDots() {
      if (!wantDots || !dotsWrap) return;
      const dots = dotsWrap.querySelectorAll('.cbh-r1__dot');
      if (!dots.length) return;
      const i = Math.round(track.scrollLeft / Math.max(1, pageWidth()));
      dots.forEach((d, idx) => d.setAttribute('aria-current', idx === i ? 'true' : 'false'));
    }

    // Keep UI updates off the scroll thread.
    let _scrollTick = false;
    const onScroll = () => {
      if (_scrollTick) return;
      _scrollTick = true;
      requestAnimationFrame(() => {
        _scrollTick = false;
        updateNav();
        updateScrollbar();
        updateDots();
      });
    };

    // Drag-to-scroll (desktop)
    (function enableDragScroll() {
      let isDown = false;
      let startX = 0;
      let startLeft = 0;
      let pointerId = null;
      let moved = false;
      const threshold = 6;

      function endDrag() {
        if (!isDown) return;
        isDown = false;
        pointerId = null;
        // keep is-dragging for a tick to prevent click text selection
        track.classList.remove('is-dragging');
        track.classList.remove('is-pressing');
        track.style.scrollSnapType = '';
      }

      track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return; // left click only
        // Only enable for mouse/pen; touch already scrolls naturally
        if (e.pointerType === 'touch') return;

        // prevent accidental text selection while starting a drag
        e.preventDefault();

        isDown = true;
        moved = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startLeft = track.scrollLeft;

        track.classList.add('is-pressing');
        // Temporarily disable snap while dragging for smoother movement
        track.style.scrollSnapType = 'none';
        track.setPointerCapture(pointerId);
      });

      track.addEventListener('pointermove', (e) => {
        if (!isDown || e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < threshold) return;

        moved = true;
        track.classList.add('is-dragging');
        e.preventDefault();
        track.scrollLeft = startLeft - dx;
      }, { passive: false });

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', endDrag);

      // Prevent native drag interactions
      track.addEventListener('dragstart', (e) => e.preventDefault());

      // Prevent “click” after a drag (stops accidental text selection / focus jumps)
      track.addEventListener('click', (e) => {
        if (!moved) return;
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }, true);
    })();

    // Initial measure + react to resizes
    measure();
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleMeasure).catch(() => {});
    }
    track.addEventListener('scroll', onScroll, { passive: true });

    // Controls
    prev && prev.addEventListener('click', () => scrollByPage(-1));
    next && next.addEventListener('click', () => scrollByPage(1));

    // Keyboard (when track focused)
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByPage(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByPage(1);
      }
    });

    // (scroll updates handled via rAF-throttled onScroll)

    // Autoplay
    let autoplayTimer = null;
    function startAutoplay() {
      if (!wantAutoplay) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => {
        const atEnd = track.scrollLeft >= (_max || 0) - 2;
        if (atEnd) {
          if (wantLoop) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          } else {
            stopAutoplay();
            return;
          }
        } else {
          scrollByPage(1);
        }
      }, 3500);
    }
    function stopAutoplay() {
      if (autoplayTimer) window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    // Pause autoplay on interaction
    ['pointerdown', 'touchstart', 'keydown', 'mouseenter', 'focusin', 'wheel'].forEach((evt) => {
      track.addEventListener(evt, stopAutoplay, { passive: true });
    });

    // Rebuild dots on resize (columns change)
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        buildDots();
        updateScrollbar();
        updateNav();
      }, 150);
    });

    function initialSync() {
      buildDots();
      updateScrollbar();
      updateDots();
      updateNav();
    }

    // Run sync a few times to catch late font/image/layout changes
    initialSync();
    requestAnimationFrame(initialSync);
    window.setTimeout(initialSync, 60);
    window.setTimeout(initialSync, 250);
    window.addEventListener('load', initialSync, { once: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(initialSync).catch(() => {});
    }

    startAutoplay();
  }

  function boot() {
    document.querySelectorAll('[data-cbh-review-one]').forEach(init);
  }


  // Theme editor: re-init when a section is reloaded
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target && e.target.querySelector && e.target.querySelector('[data-cbh-review-one]');
    if (el) init(el);
  });

  function scheduleBoot() {
  var started = false;
  var start = function () {
    if (started) return;
    started = true;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function(){ scheduleBoot(); }, { timeout: 2500 });
    } else {
      setTimeout(function(){ scheduleBoot(); }, 250);
    }
  };

  if ('IntersectionObserver' in window) {
    var els = document.querySelectorAll('[data-cbh-review-one]');
    if (els && els.length) {
      var io = new IntersectionObserver(function(entries){
        for (var i=0;i<entries.length;i++){
          if (entries[i].isIntersecting) { io.disconnect(); start(); break; }
        }
      }, { rootMargin: '300px 0px' });
      for (var j=0;j<els.length;j++){ io.observe(els[j]); }
      setTimeout(start, 2800);
      return;
    }
  }
  start();
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleBoot);
  } else {
    scheduleBoot();
  }
})();
